import _ from 'lodash';
import router from '@/routers';
import type { IMessageInput } from '@eureka/chat-anywhere';
import { CopilotPlan, type CopilotMessageContext, PatsnapCopilotService } from '@analytics/patsnap-copilot/src/patsnap-copilot-service.interface';
import { AbstractEurekaCopilotService } from '@eureka/services/src/eureka-copilot-service';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { WuJieBus } from '@analytics/patsnap-copilot/src/wujie-bus';
import { I18nService } from './i18n-service';
import type { IMessage, MessageTurn } from '@/types';
import type { Event } from '@patsnap/tahiti-core/common/event';
import { Emitter } from '@patsnap/tahiti-core/common/event';
import { getProjectHistory } from '@/infrastructure/api-functions/create';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';

@injectable()
export class ProjectCopilotServiceImpl extends AbstractEurekaCopilotService implements PatsnapCopilotService {
    constructor(
        @inject(WuJieBus)
        private readonly _wujieBus: WuJieBus,
        @inject(I18nService)
        private readonly _i18nService: I18nService
    ) {
        super();
        this._wujieBus.$on('copilot-loaded', (isLoaded: boolean) => {
            if (isLoaded) {
                this._wujieBus.$emit('copilot-ready', true);
            }
        });
        this.placeholder = this._i18nService.t('chat.placeholder');
    }

    disableFeedback = true;
    source = 'PROJECT_CHAT';
    webControl = false;
    lastInput = '';

    protected onSendMessageEmitter = new Emitter<MessageTurn>();

    onSendMessage: Event<MessageTurn> = this.onSendMessageEmitter.event;

    historyIdentifier() {
        return '';
    }

    scrollToEnd() {
        this._wujieBus.$emit('copilot-scroll-to-end');
    }

    getFactoryPsHttpSSE() {
        return injectService(FactoryPsHttpSSE) as FactoryPsHttpSSE;
    }

    historyService = {
        isExpand: true,
        save: () => { },
        load: async() => {
            const copilotService = injectService<PatsnapCopilotService>(PatsnapCopilotService) as unknown as ProjectCopilotServiceImpl;
            if (!copilotService.jobId) return [];
            const { modules } = await getProjectHistory(copilotService.jobId || '', 'SESSION_CHAT');
            const turns = _.get(_.find(modules, { module_type: 'SESSION_CHAT' }), 'records');
            setTimeout(() => {
                copilotService.scrollToEnd();
            }, 100);
            return _.map(turns, turn => {
                const input = JSON.parse(turn.input);
                const output = JSON.parse(turn.output);
                return {
                    input: {
                        text: input.query,
                    },
                    reply: {
                        detail: {
                            message: {
                                ...output,
                            },
                            renderer: 'research_follow',
                        },
                    },
                }
            })
        },
        clean: () => { },
    }

    actionCallback(type: 'JUMP_TO_SOURCE', data: Record<string, unknown>) { }

    pageParamFunc() {
        return {};
    }

    contextValidator(context: CopilotMessageContext) {
        return true;
    }

    getSocketType() {
        return 'TECH_RESEARCH';
    }

    getSSEConfig() {
        return {
            path: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/sse/tech-research/chat`,
        };
    }

    patsnapCopilotUsage = {
        enabled: true,
        currentAmount: 0,
        maxAmount: 10000000,
        plan: CopilotPlan.B,
        webAccess: false,
    };

    private defaultMessage: any = null;

    setDefaultMessage(message: IMessageInput) {
        this.defaultMessage = message;
    }

    sendMessage(message?: IMessageInput) {
        this._wujieBus.$emit('send-message', message);
    }

    newSession(message?: IMessageInput) {
        this._wujieBus.$emit('clean-message');
        this._wujieBus.$emit('copilot-reload-history');
        if (message) {
            this.sendMessage(message);
        } else {
            this.sendMessage(this.defaultMessage);
        }
    }

    toolbarList() {
        return [{
            title: this._i18nService.t('chat.askNew'),
            iconHTML: '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 24 24"><path d="M13 11h4a1 1 0 0 1 0 2h-4v4a1 1 0 0 1-2 0v-4H7a1 1 0 0 1 0-2h4V7a1 1 0 0 1 2 0v4z"></path></svg>',
            onClick: async() => {
                await router.push({
                    name: 'chat',
                    query: {
                        ts: new Date().getTime(),
                    },
                })
                const copilotService = injectService<PatsnapCopilotService>(PatsnapCopilotService) as unknown as ProjectCopilotServiceImpl;
                if (!copilotService.jobId) return [];
                copilotService._wujieBus.$emit('copilot-input-disabled', false);
                copilotService.setJobId('');
                copilotService.newSession();
            },
        }];
    }

    /**
     * 发送消息前的钩子，后端需要根据上一条消息的内容进行处理
     */
    onBeforeSendMessageHook(input: IMessage, turns: unknown[]) {
        if (!input.text) { return input }
        const lastMessage = _.last(_.compact(_.map(turns, item => _.get(item, 'reply.detail.message'))));
        if (lastMessage) {
            const copilotService = injectService<PatsnapCopilotService>(PatsnapCopilotService) as unknown as BoardCopilotServiceImpl;
            input.data = {
                above: JSON.stringify(lastMessage),
                business_id: copilotService.jobId,
            }
        }
        return input
    }

    intentExtDataFunc() {
        return {
            research_follow: {
                click: () => {
                    return this.generateCb();
                },
                copilotTitle: '',
            },
        }
    }

    generateCb = _.noop;
    onGenerate(cb: () => void) {
        this.generateCb = cb;
    }
}
